import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Typography, Spin, Flex, Input, Dropdown } from "antd"
import { MessageOutlined, CloseOutlined, MoreOutlined, EditOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { toaster, cappedToaster } from "components/common/Toast"
import styles from "../../style.module.css"

dayjs.extend(relativeTime)

const { Text } = Typography

function parseUtc(isoString) {
  return dayjs(isoString + "Z")
}

function formatSessionTime(isoString) {
  if (!isoString) return ""
  const date = parseUtc(isoString)
  const now = dayjs()
  const diffHours = now.diff(date, "hour")
  if (diffHours < 24) return date.fromNow()
  if (diffHours < 48) return "Yesterday"
  return date.format("MMM D")
}

function truncatePreview(text, maxLength = 60) {
  if (!text) return ""
  return text.length > maxLength ? text.slice(0, maxLength).trimEnd() + "…" : text
}

function SessionItem({ session, isActive, onSelect, onRename, onDelete, t }) {
  const [renamingSessionId, setRenamingSessionId] = useState(null)
  const [renameValue, setRenameValue] = useState("")
  const [originalRenameValue, setOriginalRenameValue] = useState("")
  const [actionLoading, setActionLoading] = useState(false)
  const { showConfirm, modal: confirmModal } = useNewConfirmModal()

  const displayText = session.title
    ? truncatePreview(session.title)
    : truncatePreview(session.preview) || t("ai_dashboard.new_conversation")

  const isRenaming = renamingSessionId === session.session_id

  function startRename() {
    const current = session.title || session.preview || ""
    setRenameValue(current)
    setOriginalRenameValue(current)
    setRenamingSessionId(session.session_id)
  }

  function cancelRename() {
    setRenamingSessionId(null)
    setRenameValue("")
  }

  function submitRename() {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === originalRenameValue.trim()) return
    setActionLoading(true)
    onRename(session.session_id, trimmed)
      .then(() => toaster.success({ title: t("ai_dashboard.rename_session_success") }))
      .catch(() => toaster.error({ title: t("ai_dashboard.rename_session_error") }))
      .finally(() => {
        setActionLoading(false)
        setRenamingSessionId(null)
        setRenameValue("")
      })
  }

  function handleRenameKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      submitRename()
    }
    if (e.key === "Escape") cancelRename()
  }

  function handleDelete() {
    showConfirm(
      {
        title: t("ai_dashboard.delete_session_title"),
        message: t("ai_dashboard.delete_session_message"),
        confirmLabel: t("ai_dashboard.delete")
      },
      () => {
        setActionLoading(true)
        onDelete(session.session_id)
          .then(() => cappedToaster("delete-chat-session-success", "success", { title: t("ai_dashboard.delete_session_success") }))
          .catch(() => cappedToaster("delete-chat-session-error", "error", { title: t("ai_dashboard.delete_session_error") }))
          .finally(() => setActionLoading(false))
      }
    )
  }

  const menuItems = [
    {
      key: "rename",
      label: t("ai_dashboard.rename"),
      icon: <EditOutlined />,
      onClick: startRename
    },
    {
      key: "delete",
      label: t("ai_dashboard.delete"),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete
    }
  ]

  return (
    <>
      {confirmModal}
      <div className={`${styles.sessionItem} ${isActive ? styles.sessionItemActive : ""}`}>
        {isRenaming ? (
          <Flex align="center" gap={4} className={styles.sessionRenameRow}>
            <Input
              size="small"
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              disabled={actionLoading}
              className={styles.sessionRenameInput}
            />
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined />}
              loading={actionLoading}
              disabled={!renameValue.trim() || renameValue.trim() === originalRenameValue.trim()}
              onClick={submitRename}
              className={styles.sessionRenameConfirm}
            />
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={cancelRename}
              disabled={actionLoading}
              className={styles.sessionRenameCancel}
            />
          </Flex>
        ) : (
          <Flex align="flex-start" gap={8}>
            <button type="button" className={styles.sessionItemBtn} onClick={() => onSelect(session.session_id)}>
              <Flex vertical align="flex-start">
                <Text className={styles.sessionPreview} ellipsis={{ rows: 2 }}>
                  {displayText}
                </Text>
                <Text type="secondary" className={styles.sessionTime}>
                  {formatSessionTime(session.last_message_at || session.started_at)}
                </Text>
              </Flex>
            </button>
            <Flex align="center" gap={4} className={styles.sessionItemActions} flex-shrink="0">
              {session.message_count > 0 && <span className={styles.sessionBadge}>{session.message_count}</span>}
              <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
                <Button
                  type="text"
                  size="small"
                  icon={<MoreOutlined />}
                  className={styles.sessionMenuBtn}
                  onClick={(e) => e.stopPropagation()}
                  disabled={actionLoading}
                />
              </Dropdown>
            </Flex>
          </Flex>
        )}
      </div>
    </>
  )
}

SessionItem.propTypes = {
  session: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}

export default function ChatSessionsSidebar({
  sessionsList,
  sessionsLoading,
  sessionsError,
  hasMoreSessions,
  currentSessionId,
  onSelectSession,
  onLoadMore,
  onRetry,
  onClose,
  onRenameSession,
  onDeleteSession
}) {
  const { t } = useTranslation()

  function renderContent() {
    if (sessionsError) {
      return (
        <Flex vertical align="center" gap={4} className={styles.sessionsError}>
          <Text type="secondary" className={styles.textSm}>
            {t("ai_dashboard.sessions_load_error")}
          </Text>
          <Button size="small" type="link" onClick={onRetry}>
            {t("ai_dashboard.retry")}
          </Button>
        </Flex>
      )
    }
    if (sessionsLoading && sessionsList.length === 0) {
      return (
        <Flex justify="center" align="center" className={styles.sessionsSpinner}>
          <Spin size="small" />
        </Flex>
      )
    }
    if (sessionsList.length === 0) {
      return (
        <Flex vertical align="center" gap={8} className={styles.sessionsEmpty}>
          <MessageOutlined className={styles.sessionsEmptyIcon} />
          <Text type="secondary" className={styles.textSm}>
            {t("ai_dashboard.no_past_sessions")}
          </Text>
        </Flex>
      )
    }
    return (
      <>
        {sessionsList.map((session) => (
          <SessionItem
            key={session.session_id}
            session={session}
            isActive={session.session_id === currentSessionId}
            onSelect={onSelectSession}
            onRename={onRenameSession}
            onDelete={onDeleteSession}
            t={t}
          />
        ))}

        {hasMoreSessions && (
          <Button type="link" size="small" loading={sessionsLoading} onClick={onLoadMore} className={styles.loadMoreBtn}>
            {t("ai_dashboard.load_more_sessions")}
          </Button>
        )}
      </>
    )
  }

  return (
    <Flex vertical className={styles.sessionsSidebar}>
      <Flex align="center" className={styles.sidebarHeader}>
        <Text className={styles.sidebarTitle}>{t("ai_dashboard.chat_history")}</Text>
        <Button type="text" size="small" icon={<CloseOutlined />} className={styles.sidebarCloseBtn} onClick={onClose} />
      </Flex>
      <Flex vertical className={styles.sessionsList}>
        {renderContent()}
      </Flex>
    </Flex>
  )
}

ChatSessionsSidebar.propTypes = {
  sessionsList: PropTypes.array.isRequired,
  sessionsLoading: PropTypes.bool.isRequired,
  sessionsError: PropTypes.bool.isRequired,
  hasMoreSessions: PropTypes.bool.isRequired,
  currentSessionId: PropTypes.string,
  onSelectSession: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRenameSession: PropTypes.func.isRequired,
  onDeleteSession: PropTypes.func.isRequired
}
